import { Injectable } from '@angular/core';
import { imageDecodeVersion, type DecodedImage, type DecodeImageOptions } from './decode-image';

// Decoded RGBA is width×height×4 — a print-res spread background runs tens of MB, so the cap is in bytes.
export const DECODE_CACHE_MAX_BYTES = 256 * 1024 * 1024;

/** URL-and-decode-mode keyed LRU over RGBA. Outlives spread switches (root-provided), so revisits skip the
 *  fetch→createImageBitmap→getImageData rasterization; in-flight decodes are deduped per URL.
 *  Holds full-size decodes only: the key has no size in it, so a reduced copy would reach a caller that
 *  asked for full size. */
@Injectable({ providedIn: 'root' })
export class DecodeImageCacheService {
  // Map insertion order doubles as recency: hits re-insert, eviction pops from the front.
  private readonly entries = new Map<string, DecodedImage>();
  private readonly pending = new Map<string, Promise<DecodedImage>>();
  private bytes = 0;
  protected readonly maxBytes: number = DECODE_CACHE_MAX_BYTES;

  getOrDecode(url: string, decode: () => Promise<DecodedImage>, options?: DecodeImageOptions): Promise<DecodedImage> {
    url = imageDecodeVersion(url, options?.preservePngRgba);
    const hit = this.entries.get(url);
    if (hit) {
      this.entries.delete(url);
      this.entries.set(url, hit);
      return Promise.resolve(hit);
    }

    const inFlight = this.pending.get(url);
    // A reduced copy belongs to the caller whose decode made it; this caller decodes on its own terms.
    if (inFlight) return inFlight.then(image => (image.downscaled ? decode() : image));

    const decoding = decode().then(
      image => {
        this.pending.delete(url);
        this.store(url, image);
        return image;
      },
      (error: unknown) => {
        this.pending.delete(url);
        throw error;
      },
    );
    this.pending.set(url, decoding);
    return decoding;
  }

  private store(url: string, image: DecodedImage): void {
    const size = image.data.byteLength;
    if (size > this.maxBytes || image.downscaled) return;

    this.entries.set(url, image);
    this.bytes += size;
    for (const [key, value] of this.entries) {
      if (this.bytes <= this.maxBytes) break;
      if (key === url) break;
      this.entries.delete(key);
      this.bytes -= value.data.byteLength;
    }
  }
}
